import React, { useMemo, useRef, useState } from 'react';
import classNames from 'classnames';
import { useRouter } from 'next/router';
import { IconButton } from '@mui/material';
import { useTranslation } from 'next-i18next';

import Link from 'next/link';
import { SwiperSlide } from 'swiper/react';
import RecitersListItem from '../list/item';
import AppTitle from '@/components/common/title';
import { routes } from '@/infrastructure/const/routes';
import RecitersFeaturedCarouselItem from './item/index';
import { Reciter } from '@/infrastructure/class/reciter';
import AppContainer from '@/components/layout/container';
import { IReciter } from '@/infrastructure/interfaces/i-reciter';
import AppCarusel, { IAppCarouselRef } from '@/components/common/carousel';
import { ChevronLeftRounded, ChevronRightRounded } from '@mui/icons-material';
import { sortArrayByFieldName } from '@/utils/helpers/sort-array-by-field-name';
import s from './styles.module.scss';
import { Swiper } from 'swiper/types';

interface IRecitersCarouselProps {
  items: IReciter[];
  secondType?: boolean;
  title?: string;
  className?: string;
  fullWidth?: boolean;
}

const RecitersFeaturedCarousel: React.FC<IRecitersCarouselProps> = (props) => {
  const { className, items, secondType = false, fullWidth = false } = props;
  const [isAtStart, setIsAtStart] = useState(true);
  const [isAtEnd, setIsAtEnd] = useState(false);

  const { t: tCommon } = useTranslation('common');
  const { t: tButtons } = useTranslation('buttons');

  const { title = tCommon('featuredReciters') } = props;

  const router = useRouter();
  const { locale } = router;

  const carouselRef = useRef<IAppCarouselRef>(null);

  const reciters = useMemo<Reciter[]>(
    () =>
      sortArrayByFieldName(
        items
          .filter((reciter) => !reciter.is_hidden)
          .map((reciter) => new Reciter(reciter, locale)),
        'featuredOrder'
      ),
    [items, locale]
  );

  const first3Reciters = reciters.slice(0, 3).map((item) => item.featuredOrder);

  const hasItems = !!reciters.length;
  const hasMoreItems = secondType ? reciters.length > 5 : reciters.length > 3;

  const carouselWrapperClassNames = classNames(
    s['carousel-wrapper'],
    className
  );
  const containerClassNames = classNames({
    [s['container']]: !fullWidth,
  });
  const carouselClassNames = classNames(s['carousel'], {
    [s['carousel-full-width']]: fullWidth,
  });
  const slideClassNames = classNames(s['slide'], {
    [s['second']]: secondType,
  });
  const noItemsClassNames = classNames(s['no-item'], {
    [s['has-items']]: hasItems,
  });

  const carouselWithControlsClassNames = classNames(
    s['carousel-with-controls'],
    {
      [s['featured']]: !secondType,
    }
  );

  const featuredLeftBtnClassNames = classNames(
    s['btn'],
    s['featured-left-btn']
  );
  const featuredRightBtnClassNames = classNames(
    s['btn'],
    s['featured-right-btn']
  );

  const onPrev = () => {
    if (carouselRef.current) {
      carouselRef.current.prevSlide();
    }
  };
  const onNext = () => {
    if (carouselRef.current) {
      carouselRef.current.nextSlide();
    }
  };

  const handleSlideChange = (swiper: Swiper) => {
    setIsAtStart(swiper?.isBeginning || false);
    setIsAtEnd(swiper?.isEnd || false);
  };

  return (
    <div className={carouselWrapperClassNames}>
      <AppContainer className={containerClassNames}>
        <AppTitle title={title} />
        <div className={s['info']}>
          <p className={s['subtitle']}>
            {tCommon('featuredRecitersDescription')}
          </p>
          <Link
            className={s['link']}
            href={routes.reciters}
            prefetch={false}
            passHref
          >
            {tButtons('seeAll')}
          </Link>
        </div>
      </AppContainer>
      <div className={carouselWithControlsClassNames}>
        {hasMoreItems && !secondType && (
          <IconButton
            aria-label="slider swipe left"
            className={featuredLeftBtnClassNames}
            onClick={onPrev}
          >
            <ChevronLeftRounded />
          </IconButton>
        )}
        <AppCarusel
          className={carouselClassNames}
          ref={carouselRef}
          loop={!secondType}
          onSlideChange={handleSlideChange}
          spaceBetween={!secondType ? 16 : undefined}
          breakpoints={!secondType ? { 601: { slidesPerView: 3 } } : undefined}
        >
          {reciters.map((reciter) => (
            <SwiperSlide className={slideClassNames} key={reciter.slug}>
              {secondType ? (
                <RecitersListItem data={reciter} />
              ) : (
                <RecitersFeaturedCarouselItem
                  imageLoadingEager={first3Reciters.includes(
                    reciter.featuredOrder
                  )}
                  data={reciter}
                />
              )}
            </SwiperSlide>
          ))}
        </AppCarusel>
        {hasMoreItems && !secondType && (
          <IconButton
            aria-label="slider swipe right"
            className={featuredRightBtnClassNames}
            onClick={onNext}
          >
            <ChevronRightRounded />
          </IconButton>
        )}
      </div>
      {fullWidth && (
        <AppContainer className={s['full-width-controls']}>
          <IconButton
            disabled={isAtStart}
            className={s['btn']}
            onClick={onPrev}
            aria-label="slider swipe left"
          >
            <ChevronLeftRounded />
          </IconButton>
          <IconButton
            disabled={isAtEnd}
            className={s['btn']}
            onClick={onNext}
            aria-label="slider swipe right"
          >
            <ChevronRightRounded />
          </IconButton>
        </AppContainer>
      )}
      <div className={noItemsClassNames}>
        <AppTitle title={tCommon('noItems')} />
      </div>
    </div>
  );
};

export default RecitersFeaturedCarousel;
